<template>
  <div class="document-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
        <span>Document</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
     
    <div class="document-element" @click="uploadDocument">
      <div v-if="element.link || localDocumentUrl" class="document-container">
        <div class="document-preview" @click.stop="previewDocument">
          <div class="document-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <path d="M14 2v6h6"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
              <path d="M10 9H8"/>
            </svg>
          </div>
          <div class="document-info">
            <span class="document-filename">{{ getFileName(localDocumentUrl || element.link) }}</span>
            <span class="document-type">PDF Document</span>
            <span class="document-action-hint">Cliquez pour prévisualiser</span>
          </div>
        </div>
        <div class="document-overlay">
          <button class="preview-document-btn" @click.stop="previewDocument" title="Prévisualiser le document">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <button class="change-document-btn" @click.stop="uploadDocument" title="Changer le document" :disabled="isUploading">
            <svg v-if="!isUploading" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <path d="M14 2v6h6"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
              <path d="M10 9H8"/>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
              <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
            </svg>
          </button>
        </div>
      </div>
      <div v-else class="document-placeholder" :class="{ 'uploading': isUploading }">
        <svg v-if="!isUploading" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
          <path d="M14 2v6h6"/>
          <path d="M16 13H8"/>
          <path d="M16 17H8"/>
          <path d="M10 9H8"/>
        </svg>
        <svg v-else width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
          <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
        <p v-if="!isUploading">Cliquez pour ajouter un document PDF</p>
        <p v-else>Upload en cours...</p>
      </div>
      <input type="file" ref="documentUpload" style="display: none" accept=".pdf,application/pdf" @change="handleDocumentUpload" />
    </div>

    <!-- Nouveaux champs modifiables -->
    <div v-if="element.link || localDocumentUrl" class="document-settings">
      <div class="setting-input-group">
        <label>Nom du fichier (Optionnel)</label>
        <input 
          type="text" 
          :value="element.filename || ''" 
          @blur="updateField('filename', $event.target.value)"
          @keyup.enter="updateField('filename', $event.target.value)" 
          placeholder="ex: facture_janvier.pdf" 
          class="setting-input"
        />
      </div>
      <div class="setting-input-group">
        <label>Légende (Caption)</label>
        <input 
          type="text" 
          :value="element.caption || ''" 
          @blur="updateField('caption', $event.target.value)"
          @keyup.enter="updateField('caption', $event.target.value)"
          placeholder="ex: Voici votre facture" 
          class="setting-input"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios'
import { apiBaseUrl } from '@/config'
import { consoleLog, consoleError } from '@/utils/logger'

export default {
  name: 'DocumentElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isUploading: false,
      localDocumentUrl: null // URL locale pour afficher le document sélectionné
    }
  },
  methods: {
    // Méthode pour mettre à jour un champ spécifique
    updateField(field, value) {
      const updatedElement = { ...this.element, [field]: value };
      this.$emit('update', updatedElement);
    },

    // Méthode pour déclencher l'upload de document
    uploadDocument() {
      // Empêcher l'upload si on est en train de prévisualiser
      if (event && event.target.closest('.document-preview')) {
        return;
      }
      
      // Créer un élément input file dynamique
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.pdf,application/pdf';
      fileInput.style.display = 'none';
      
      // Ajouter l'événement de changement
      fileInput.addEventListener('change', (event) => {
        this.handleDocumentUpload(event);
      });
      
      // Ajouter au DOM, déclencher le clic, puis supprimer
      document.body.appendChild(fileInput);
      fileInput.click();
      setTimeout(() => {
        document.body.removeChild(fileInput);
      }, 1000);
    },

    // Méthode pour prévisualiser le document
    previewDocument() {
      const documentUrl = this.localDocumentUrl || this.element.link;
      
      if (!documentUrl) {
        this.$emit('notification', {
          message: "Aucun document à prévisualiser",
          type: "error"
        });
        return;
      }

      try {
        // Ouvrir le PDF dans un nouvel onglet
        window.open(documentUrl, '_blank', 'noopener,noreferrer');
        
        this.$emit('notification', {
          message: "Ouverture du document en cours...",
          type: "success"
        });
      } catch (error) {
        consoleError('Erreur lors de l\'ouverture du document:', error);
        this.$emit('notification', {
          message: "Impossible d'ouvrir le document",
          type: "error"
        });
      }
    },
    
    // Méthode pour gérer l'upload de document
    async handleDocumentUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Vérifier que c'est bien un PDF
      const fileName = file.name.toLowerCase();
      const allowedTypes = ['application/pdf'];
      const allowedExtensions = ['.pdf'];
      
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidType || !isValidExtension) {
        this.$emit('notification', {
          message: "Format non autorisé. Seuls les fichiers PDF sont acceptés.",
          type: "error"
        });
        return;
      }
      
      // Vérifier la taille du fichier (15 Mo maximum)
      const maxSizeInBytes = 15 * 1024 * 1024; // 15 Mo en bytes
      if (file.size > maxSizeInBytes) {
        const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        this.$emit('notification', {
          message: `Le document est trop volumineux (${fileSizeInMB} Mo). Taille maximum autorisée : 15 Mo`,
          type: "error"
        });
        return;
      }
      
      // Démarrer l'état de chargement
      this.isUploading = true;
      
      // Créer une URL locale pour afficher le document immédiatement
      this.localDocumentUrl = URL.createObjectURL(file);
       
      try {
        // Créer FormData pour l'upload
        const formData = new FormData();
        formData.append('docfromplayground', file);
        consoleLog('le fichier a uploader ', file);
        
        // Récupérer le token d'authentification
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Session expirée, veuillez vous reconnecter');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
        // Configuration des headers avec timeout étendu pour les gros fichiers
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          // Timeout de 2 minutes pour les gros fichiers
          timeout: 120000, // 120 secondes = 2 minutes
          // Fonction de progression pour suivre l'upload
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              consoleLog(`Upload en cours: ${percentCompleted}%`);
              
              // Émettre une notification de progression
              if (percentCompleted % 5 === 0) { // Tous les 25%
                this.$emit('notification', {
                  message: `Upload en cours: ${percentCompleted}%`,
                  type: "info"
                });
              }
            }
          }
        };
        
        consoleLog('🚀 Début upload document vers /strong/upload/document/block');
        consoleLog('📁 Taille du fichier:', (file.size / (1024 * 1024)).toFixed(2), 'Mo');
        
        this.$emit('notification', {
          message: "Upload en cours, merci de patienter...",
          type: "success"
        });
        
        // Envoyer le document au serveur avec await
        const response = await axiosPeelo.post(
          '/strong/upload/document/block',
          formData,
          config
        );
        
        consoleLog('✅ APRES UPLOAD DOCUMENT DONE:', response.data);
        
        if (response.data && response.data.success) {
          // Construire l'URL complète du document avec apiBaseUrl
          const documentUrl = `${apiBaseUrl}/${response.data.filePath}`;
          
          consoleLog('📄 URL du document:', documentUrl);

          await new Promise(resolve => setTimeout(resolve, 2000));

          
          // Mettre à jour l'élément avec le lien du document du serveur
          const updatedElement = { ...this.element, link: documentUrl };
          
          // Émettre l'événement de mise à jour IMMÉDIATEMENT
          this.$emit('update', updatedElement);
          
          consoleLog('✅ Élément mis à jour avec succès');
          
          // Émettre une notification de succès
          this.$emit('notification', {
            message: "Document uploadé avec succès !",
            type: "success"
          });
          
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'upload');
        }
        
      } catch (error) {
        consoleError('❌ Erreur lors de l\'upload du document:', error);
        
        let errorMessage = 'Erreur lors de l\'upload du document';
        
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorMessage = 'Timeout: Le fichier est trop volumineux ou la connexion est lente. Réessayez avec un fichier plus petit.';
        } else if (error.response) {
          // Erreur de réponse du serveur
          consoleError('Erreur serveur:', error.response.status, error.response.data);
          errorMessage = error.response.data?.message || 
                        error.response.data?.error || 
                        `Erreur serveur (${error.response.status})`;
        } else if (error.request) {
          // Erreur de réseau
          consoleError('Erreur réseau:', error.request);
          errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
        } else {
          // Autre erreur
          consoleError('Autre erreur:', error.message);
          errorMessage = error.message || errorMessage;
        }
        
        // Émettre une notification d'erreur
        this.$emit('notification', {
          message: errorMessage,
          type: "error"
        });
        
        // Nettoyer l'URL locale en cas d'erreur
        if (this.localDocumentUrl) {
          URL.revokeObjectURL(this.localDocumentUrl);
          this.localDocumentUrl = null;
        }
        
      } finally {
        // Arrêter l'état de chargement seulement ici
        this.isUploading = false;
        consoleLog('🏁 Upload terminé (succès ou erreur)');
      }
    },

    // Méthode pour attendre que le fichier soit disponible
    async waitForFileAvailability(fileUrl, maxAttempts = 10, delayMs = 1000) {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          consoleLog(`Vérification de disponibilité du fichier (tentative ${attempt}/${maxAttempts})`);
          
          // Essayer de faire une requête HEAD pour vérifier si le fichier existe
          const response = await fetch(fileUrl, { 
            method: 'HEAD',
            cache: 'no-cache'
          });
          
          if (response.ok) {
            consoleLog('✅ Fichier disponible');
            this.$emit('notification', {
              message: "Fichier prêt !",
              type: "success"
            });
            return true;
          }
          
          consoleLog(`❌ Fichier non disponible (statut: ${response.status})`);
          
        } catch (error) {
          consoleLog(`❌ Erreur de vérification (tentative ${attempt}): ${error.message}`);
        }
        
        // Attendre avant la prochaine tentative (sauf pour la dernière)
        if (attempt < maxAttempts) {
          consoleLog(`⏳ Attente de ${delayMs}ms avant la prochaine vérification...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
          
          // Augmenter progressivement le délai
          delayMs = Math.min(delayMs * 1.2, 3000);
        }
      }
      
      // Si après toutes les tentatives le fichier n'est toujours pas disponible
      consoleLog('⚠️ Timeout: fichier non disponible après toutes les tentatives');
      this.$emit('notification', {
        message: "Le fichier a été uploadé mais prend du temps à être disponible. Veuillez réessayer dans quelques instants.",
        type: "warning"
      });
      
      return false;
    },

    getFileName(url) {
      if (!url) return 'Document PDF';
      const parts = url.split('/');
      return parts[parts.length - 1];
    }
  },
  
  // Nettoyer l'URL locale quand le composant est détruit
  beforeUnmount() {
    if (this.localDocumentUrl) {
      URL.revokeObjectURL(this.localDocumentUrl);
    }
  }
}
</script>

<style scoped>
.document-element-wrapper {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.document-element-wrapper:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028;
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #daffd1;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.element-type-badge svg {
  color: #daffd1;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 255, 209, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #daffd1;
  border-color: #daffd1;
  transform: scale(1.05);
}

.action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

.document-element {
  padding: 16px;
  cursor: pointer;
}

.document-placeholder {
  background: #ffffff;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #143028;
  border: 2px dashed #93e68e;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.document-placeholder:hover {
  background: #daffd1;
  border-color: #6ed168;
}

.document-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}

.document-container {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(20, 48, 40, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
}

.document-container:hover {
  background: #daffd1;
  border-color: rgba(20, 48, 40, 0.2);
}

.document-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.document-preview:hover {
  transform: translateY(-1px);
}

.document-icon {
  flex-shrink: 0;
  color: #143028;
}

.document-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.document-filename {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
  word-break: break-all;
  margin-bottom: 2px;
}

.document-type {
  color: #6b7280;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.document-action-hint {
  color: #143028;
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0.8;
}

.document-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 16px;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 12px;
}

.document-container:hover .document-overlay {
  opacity: 1;
}

.preview-document-btn,
.change-document-btn {
  background: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.preview-document-btn {
  color: #143028;
}

.preview-document-btn:hover {
  background: #d8ffd1;
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(20, 48, 40, 0.3);
}

.change-document-btn {
  color: #143028;
}

.change-document-btn:hover {
  background: #d8ffd1;
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(20, 48, 40, 0.3);
}

.change-document-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.uploading-spinner {
  animation: spin 1s linear infinite;
}

.document-placeholder.uploading {
  background: linear-gradient(135deg, #f8fcf8 0%, #e8f9e6 100%);
  border-color: #6ed168;
  color: #143028;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.document-settings {
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.setting-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.setting-input-group label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4b5563;
}

.setting-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1f2937;
  background-color: #f9fafb;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.setting-input:focus {
  outline: none;
  border-color: #143028;
  box-shadow: 0 0 0 3px rgba(20, 48, 40, 0.1);
  background-color: #ffffff;
}
</style> 